import {action, computed, makeObservable, observable, reaction} from "mobx";
import ApiResponseHandler from "@eryxcoop/appyx-comm/src/errors/ApiResponseHandler";
import errorToast from "../../../components/toast/ErrorToast";
import SaporeForm from "../../../forms/SaporeForm";
import MultipleOptionsField from "../../../forms/fields/MultipleOptionField";
import DeliverySiteMultipleOptionsField from "../../../forms/fields/DeliverySiteMultipleOptionField";
import successToast from "../../../components/toast/SuccessToast";
import RoleAdapter from "../../../adapters/RoleAdapter";

export default class AssignRolesFeature {
    constructor(application, navigator, anEmail) {
        this._application = application;
        this._navigator = navigator;
        this._loaded = false;
        this._activeDialog = false
        this._rolesOptions = [];
        this._customersOptions = [];
        this._deliverySitesOptions = [];
        this._form = null;
        this._emailFromUser = anEmail;
        this._deliverySiteUserRoles = null

        makeObservable(this, {
            _loaded: observable,
            loaded: computed,
            _form: observable,
            form: computed,
            _rolesOptions: observable,
            rolesOptions: computed,
            _customersOptions: observable,
            customersOptions: computed,
            _deliverySitesOptions: observable,
            deliverySitesOptions: computed,
            _deliverySiteUserRoles: observable,
            deliverySiteUserRoles: computed,
            isFormValid: computed,
            _finishLoading: action,
        })

        reaction(
            () => this.selectedCustomers(),
            async (selectedCustomers) => {
                await this._deliverySitesForCustomers();
            }
        );

    }

    async load() {
        await this._allRoles()
        await this._allCustomers()
        await this.getUserWithRoles()

        this._createForm()

        await this._deliverySitesForCustomers()
        this._finishLoading()
    }

    async updateFormOptions() {
        this.updateOptionFields()
    }

    async aValueChanged() {
        if (this.canEditRoles()) {
            await this.editRoles()
        }
    }

    updateOptionFields() {
        //const fields = this.form.fields()
        //fields["roles"].updateOptions(this._rolesOptions["roles"])
    }

    handleCustomerChipDelete(removedValue) {
        const selectedDeliverySites = this.selectedDeliverySites()

        const updatedDeliverySites = selectedDeliverySites.filter(deliverySite => deliverySite.businessName !== removedValue)

        this.updateFieldValue('deliverySites', updatedDeliverySites)
    }

    updateFieldValue(fieldName, value) {
        this.form.fields()[fieldName].updateValue(value)
    }

    _createForm() {
        const roles = this.rolesFieldOptions()
        const customers = this.customersFieldOptions()
        const deliverySites = this.deliverySitesFieldOptions()

        const initialRoles = this._initialRoles();
        const initialCustomers = this._initialCustomers();
        const initialDeliverySites = this._initialDeliverySites();

        const fields = {
            "roles": new MultipleOptionsField(roles, true, this, initialRoles),
            "customers": new MultipleOptionsField(customers, true, this, initialCustomers),
            "deliverySites": new DeliverySiteMultipleOptionsField(deliverySites, true, this, initialDeliverySites)
        }
        this._form = new SaporeForm(fields);
    }

    rolesFieldOptions(){
        return this.rolesOptions.map(value => {
            return {label: new RoleAdapter(value).adapt(), value: value}
        });
    }

    deliverySitesFieldOptions() {
        return this.deliverySitesOptions.map(deliverySite => {
            return {
                label: `${deliverySite.name} - ${deliverySite.address} - ${deliverySite.city}`,
                value: this._deliverySiteFieldKeyValueFor(deliverySite)
            }
        });
    }

    customersFieldOptions() {
        return this.customersOptions.map(customer => {
            return {label: `${customer.businessName} - ${customer.fantasyName} - ${customer.code}`, value: customer.businessName}
        });
    }

    userEmail(){
        return this._emailFromUser
    }

    _initialDeliverySites() {
        return this.deliverySiteUserRoles.map(deliverySiteUserRole => {
            return this._deliverySiteFieldKeyValueFor(deliverySiteUserRole.deliverySite)
        })
    }

    _initialCustomers() {
        return this.deliverySiteUserRoles.map(deliverySiteUserRole => {
            return deliverySiteUserRole.deliverySite.customer.businessName
        })
    }

    _initialRoles() {
        return this.deliverySiteUserRoles.map(deliverySiteUserRole => {
            return deliverySiteUserRole.role
        })
    }

    _deliverySiteFieldKeyValueFor(deliverySite) {
        return {
            businessName: deliverySite.customer.businessName,
            code: deliverySite.code,
        };
    }

    async _allRoles() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._rolesOptions = response.roles()
            },
            handlesError: (response) => {
                errorToast('No se pudieron obtener los roles')
            }
        });
        return this._application.apiClient().allRoles(responseHandler)
    }

    async _allCustomers() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._customersOptions = response.customers()
            },
            handlesError: (response) => {
                errorToast('No se pudieron obtener los clientes')
            }
        });
        return this._application.apiClient().getCustomers(responseHandler)
    }

    async _deliverySitesForCustomers() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                this._deliverySitesOptions = response.deliverySites()
            },
            handlesError: (response) => {
                errorToast('No se pudieron obtener los puntos de entrega')
            }
        });
        return this._application.apiClient().deliverySitesForCustomers(responseHandler, this.selectedCustomers())
    }

    async getUserWithRoles() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: async (response) => {
                this._deliverySiteUserRoles = response.deliverySiteUserRoles()
            },
            handlesError: (response) => {
                errorToast('Ha ocurrido un error al obtener el usuario.')
            }
        });
        return this._application.apiClient().userWithRolesAndPermissionsGranted(responseHandler, this.userEmail());
    }

    async editRoles() {
        const responseHandler = new ApiResponseHandler({
            handlesSuccess: (response) => {
                successToast("Se han modificado los permisos.")
            },
            handlesError: (response) => {
                errorToast("No se pudieron guardar los cambios.")
            }
        });
        const userEmail = this.userEmail()
        const deliverySites = this.selectedDeliverySites()
        const roles = this.selectedRoles()
        this._application.apiClient().updateUserRoles(userEmail, deliverySites, roles, responseHandler);
    }

    selectedCustomers() {
        return this.form.getFieldValue('customers')
    }

    selectedDeliverySites() {
        return this.form.getFieldValue('deliverySites')
    }

    selectedRoles() {
        return this.form.getFieldValue('roles')
    }

    get loaded() {
        return this._loaded;
    }

    _finishLoading() {
        this._loaded = true;
    }

    get rolesOptions(){
        return this._rolesOptions
    }

    get customersOptions(){
        return this._customersOptions
    }

    get deliverySitesOptions(){
        return this._deliverySitesOptions
    }

    get deliverySiteUserRoles(){
        return this._deliverySiteUserRoles
    }

    get isFormValid() {
        return this.form.isValid;
    }

    get form() {
        return this._form;
    }

    set form(newForm) {
        this._form = newForm;
    }

    canEditRoles() {
        return this.form && this.deliverySitesField().hasValue && this.rolesField().hasValue
    }

    getFieldByName(fieldName) {
        return this.form.getFieldByName(fieldName);
    }

    deliverySitesField() {
        return this.getFieldByName("deliverySites");
    }

    rolesField() {
        return this.getFieldByName("roles");
    }
}